import { InvestorDetailsHouseholdNotesQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import {
  INVESTOR_DETAILS_GROUP_NOTES_QUERY,
  INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY,
  INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY,
  INVESTOR_DETAILS_UPDATE_LEGAL_ENTITY_NOTES_MUTATION
} from '../queries';
import { InvestorDetailsEntityTypeEnum } from '../types';

interface DeleteNoteProps {
  isOpen: boolean;
  entityType: InvestorDetailsEntityTypeEnum;
  currentNote: { legalEntityId: string; date: string; note: string } | null;
  legalEntityNotes: InvestorDetailsHouseholdNotesQuery['getInvestor']['legalEntityNotes'];
  onClose: () => void;
}

const DeleteNote = ({ currentNote, entityType, legalEntityNotes, isOpen, onClose }: DeleteNoteProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getRefetchQueries = () => {
    switch (entityType) {
      case InvestorDetailsEntityTypeEnum.Household:
        return [INVESTOR_DETAILS_HOUSEHOLD_NOTES_QUERY];
      case InvestorDetailsEntityTypeEnum.Group:
        return [INVESTOR_DETAILS_GROUP_NOTES_QUERY];
      case InvestorDetailsEntityTypeEnum.GroupedEntity:
      case InvestorDetailsEntityTypeEnum.UngroupedEntity:
        return [INVESTOR_DETAILS_LEGAL_ENTITY_NOTES_QUERY];
      default:
        return [];
    }
  };

  const [updateLegalEntityNotes, { loading: updateLegalEntityNotesLoading }] = useMutation(INVESTOR_DETAILS_UPDATE_LEGAL_ENTITY_NOTES_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: getRefetchQueries()
  });

  const handleDeleteNote = async () => {
    try {
      setErrorMessage(null);

      const currentLegalEntityNote = legalEntityNotes.find((note) => note.legalEntityId === currentNote?.legalEntityId);
      if (!currentLegalEntityNote) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const updatedNotes = currentLegalEntityNote.notes
        .filter((note) => note.note !== currentNote?.note)
        .map((note) => ({
          date: note.date,
          note: note.note
        }));

      await updateLegalEntityNotes({
        variables: {
          data: {
            legalEntityId: currentNote?.legalEntityId,
            notes: updatedNotes
          }
        }
      });

      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    }
  };

  if (!currentNote || !legalEntityNotes?.length) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Confirm',
        onClick: handleDeleteNote,
        disabled: updateLegalEntityNotesLoading
      }}
      showCancelButton={!updateLegalEntityNotesLoading}
      showCloseIcon={!updateLegalEntityNotesLoading}
      isUseOutsideClick={!updateLegalEntityNotesLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {updateLegalEntityNotesLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this Note.</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>This cannot be undone.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteNote;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  text-align: center;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  text-align: left;
  margin-bottom: 10px;
`;
